import React, { useState } from 'react';
import { Briefcase, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';

const Careers = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: 'Part-Time System Administrator',
    location: '',
    education: '',
    yearCompleted: '',
    resumeFile: null,
    coverLetter: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const jobDetails = {
    title: 'Part-Time System Administrator',
    location: 'Remote / Hybrid',
    type: 'Part-Time',
    hours: '15–20 hours/week',
    responsibilities: [
      'Manage and maintain Windows and Linux-based systems',
      'Administer Microsoft 365, Active Directory, and Azure resources',
      'Perform network troubleshooting, configuration, and patching',
      'Deploy and monitor endpoint protection, firewalls, and backup solutions',
      'Respond to support tickets and assist end-users with technical issues',
      'Document procedures, configurations, and resolutions in internal systems',
      'Coordinate with clients on system updates, outages, or migrations',
      'Monitor system performance, uptime, and alerts via RMM tools',
    ],
    qualifications: [
      'Proven experience as a system/network administrator or similar role',
      'Familiarity with Microsoft 365 & Azure Admin Center',
      'Experience with Remote Monitoring & Management tools',
      'Knowledge of firewalls, VPNs, and security tools',
      'Basic scripting or automation experience (PowerShell preferred)',
      'Strong problem-solving and documentation skills',
      'Excellent communication with both technical and non-technical users',
    ],
    workingConditions: [
      '15–20 hours/week (flexible schedule with possible on-call rotation)',
      'Remote-first with occasional onsite (if local to client)',
      'Use of company-managed tools for secure access and communication',
    ],
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'resumeFile') {
      setFormData({
        ...formData,
        resumeFile: files[0],
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('position', formData.position);
      formDataToSend.append('location', formData.location);
      formDataToSend.append('education', formData.education);
      formDataToSend.append('yearCompleted', formData.yearCompleted);
      formDataToSend.append('coverLetter', formData.coverLetter || 'Not provided');
      if (formData.resumeFile) {
        formDataToSend.append('resumeFile', formData.resumeFile);
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/forms/career`, {
        method: 'POST',
        body: formDataToSend,
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Application submitted successfully! We\'ll review it and get back to you soon.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          position: 'Part-Time System Administrator',
          location: '',
          education: '',
          yearCompleted: '',
          resumeFile: null,
          coverLetter: '',
        });
        const fileInput = document.getElementById('resumeFile');
        if (fileInput) fileInput.value = '';
      } else {
        toast.error(data.detail || 'Failed to submit application. Please try again.');
      }
    } catch (error) {
      toast.error('An error occurred. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Toaster position="top-center" richColors />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-gray-50">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">Join Our Team</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            We're building the future of IT services. Join us in delivering exceptional technology solutions to businesses across the U.S.
          </p>
          <div className="inline-block px-6 py-3 bg-blue-100 text-[#2995FF] rounded-full text-lg font-semibold">
            Now Hiring: Part-Time System Administrator
          </div>
        </div>
      </section>

      {/* Job Details Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          {/* Job Header */}
          <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 border border-gray-200 mb-8">
            <div className="flex items-start justify-between mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center text-[#2995FF]">
                <Briefcase size={32} />
              </div>
              <span className="px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                {jobDetails.type}
              </span>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{jobDetails.title}</h2>
            <div className="flex flex-wrap gap-4 text-gray-600">
              <div className="flex items-center space-x-2">
                <MapPin size={18} className="text-[#2995FF]" />
                <span>{jobDetails.location}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Clock size={18} className="text-[#2995FF]" />
                <span>{jobDetails.hours}</span>
              </div>
            </div>
          </div>

          {/* About Company */}
          <div className="mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">About CyberTek Technology</h3>
            <p className="text-gray-600 leading-relaxed">
              CyberTek Technology is a U.S.-based IT Managed Service Provider (MSP) delivering secure, affordable, and scalable IT solutions for small and mid-sized businesses. We offer services across cloud infrastructure, cybersecurity, network design, and remote user support.
            </p>
          </div>

          {/* Position Overview */}
          <div className="mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Position Overview</h3>
            <p className="text-gray-600 leading-relaxed">
              We are seeking a Part-Time System Administrator to join our growing technical team. This role is responsible for maintaining and supporting client systems, networks, and cloud environments. You will work alongside our senior engineers to ensure client environments remain secure, up-to-date, and fully operational.
            </p>
          </div>

          {/* Key Responsibilities */}
          <div className="mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Key Responsibilities</h3>
            <ul className="space-y-3">
              {jobDetails.responsibilities.map((item, index) => (
                <li key={index} className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-[#2995FF] mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Preferred Qualifications */}
          <div className="mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Preferred Qualifications</h3>
            <ul className="space-y-3">
              {jobDetails.qualifications.map((item, index) => (
                <li key={index} className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-[#2995FF] mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Working Conditions */}
          <div className="mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Working Conditions</h3>
            <ul className="space-y-3">
              {jobDetails.workingConditions.map((item, index) => (
                <li key={index} className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-[#2995FF] mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Apply Button */}
          <div className="text-center">
            <button
              onClick={() => document.getElementById('application-form').scrollIntoView({ behavior: 'smooth' })}
              className="px-8 py-4 bg-gradient-to-r from-[#2995FF] to-[#004AAD] text-white rounded-lg font-semibold hover:shadow-lg transition-all duration-300 text-lg"
            >
              Apply for This Position →
            </button>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="application-form" className="py-20 px-4 sm:px-6 lg:px-8 bg-[#f2f2f2]">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Submit Your Application</h2>
            <p className="text-xl text-gray-600">
              Apply for the Part-Time System Administrator position
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2995FF]"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2995FF]"
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2995FF]"
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div>
                  <label htmlFor="position" className="block text-sm font-medium text-gray-700 mb-2">
                    Position
                  </label>
                  <input
                    type="text"
                    id="position"
                    name="position"
                    value={formData.position}
                    readOnly
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-2">
                  Your Location *
                </label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2995FF]"
                  placeholder="City, State"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="education" className="block text-sm font-medium text-gray-700 mb-2">
                    Highest Education *
                  </label>
                  <select
                    id="education"
                    name="education"
                    value={formData.education}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2995FF]"
                  >
                    <option value="">Select education level</option>
                    <option value="High School">High School</option>
                    <option value="Associate Degree">Associate Degree</option>
                    <option value="Bachelor's Degree">Bachelor's Degree</option>
                    <option value="Master's Degree">Master's Degree</option>
                    <option value="Doctorate">Doctorate</option>
                    <option value="Certifications">Professional Certifications</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="yearCompleted" className="block text-sm font-medium text-gray-700 mb-2">
                    Year Completed *
                  </label>
                  <input
                    type="text"
                    id="yearCompleted"
                    name="yearCompleted"
                    value={formData.yearCompleted}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2995FF]"
                    placeholder="2023"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="resumeFile" className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Resume (PDF, DOC, DOCX) *
                </label>
                <input
                  type="file"
                  id="resumeFile"
                  name="resumeFile"
                  onChange={handleChange}
                  required
                  accept=".pdf,.doc,.docx"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2995FF] file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-[#2995FF] hover:file:bg-blue-100"
                />
                <p className="text-sm text-gray-500 mt-2">Accepted formats: PDF, DOC, DOCX (Max 5MB)</p>
              </div>

              <div>
                <label htmlFor="coverLetter" className="block text-sm font-medium text-gray-700 mb-2">
                  Cover Letter / Why CyberTek?
                </label>
                <textarea
                  id="coverLetter"
                  name="coverLetter"
                  value={formData.coverLetter}
                  onChange={handleChange}
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2995FF]"
                  placeholder="Tell us about your experience and why you'd be a great fit for this role..."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-gradient-to-r from-[#2995FF] to-[#004AAD] text-white rounded-lg font-semibold hover:shadow-lg disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center space-x-2 text-lg transition-all duration-300"
              >
                {isSubmitting ? (
                  <span>Submitting...</span>
                ) : (
                  <>
                    <span>Submit Application</span>
                    <Send size={20} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Careers;
